import { Box, type SxProps, type Theme } from "@mui/material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "../../contexts/analytics/hooks";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { FONT_SIZE } from "../../theme/tokens";
import type { NurseFullRole } from "../../utils/models";
import { BreakdownRouteLink } from "./BreakdownRouteLink";
import { FooterLinkLabel } from "./FooterLinkLabel";
import { FooterRatesLink } from "./FooterRatesLink";
import type { BreakdownLinkAccess } from "./useBreakdownLinkAccess";

/**
 * The nurse whose shift this is: named with the role they work it as in the header, and by first name in
 * the footer's "Edit <name>'s wage".
 */
export type BreakdownNurse = { id: number; firstName: string; name: string; role: NurseFullRole };

type Props = {
	/** Absent where the caller has no nurse to point at: the profile link is then dropped. */
	nurse?: BreakdownNurse;
	/** An agency shift's rates come from Agency Configuration, not the department's rate rules. */
	isAgency: boolean;
	access: BreakdownLinkAccess;
};

const FOOTER_SX: SxProps<Theme> = (theme) => ({
	display: "flex",
	flexWrap: "wrap",
	alignItems: "center",
	columnGap: 3,
	rowGap: 0.5,
	px: 2,
	py: 1.25,
	borderTop: `1px solid ${theme.palette.divider}`,
	fontSize: FONT_SIZE.SECONDARY,
	displayPrint: "none",
});

/**
 * Where to go to change what the breakdown shows: the nurse's own wage (their profile — there is no
 * edit-mode URL, so it opens the view the wage is edited from), and the department's rate rules — or, for
 * an agency shift, its agency rates. Each link is dropped when the viewer could not follow it; with none
 * left, the footer is not drawn at all. Screen-only: a printed breakdown has nowhere to click.
 */
export const WageBreakdownFooterLinks: FC<Props> = ({ nurse, isAgency, access }) => {
	const { t } = useTranslation();
	const { track } = useAnalytics();
	const { chosenDepartment } = useHospitalData();
	const departmentId = chosenDepartment.department_id;

	const showProfile = nurse !== undefined && access.canOpenProfile;
	const showRates = isAgency ? access.canOpenAgencyConfiguration : access.canOpenWageSettings;
	if (!showProfile && !showRates) return null;

	return (
		<Box data-testid="wage-breakdown-footer" sx={FOOTER_SX}>
			{showProfile && (
				<BreakdownRouteLink
					to={`/${departmentId}/nurse/${nurse.id}`}
					destination="nurse_profile"
					label={<FooterLinkLabel icon="pencil" label={t("wageBreakdown.footer.editWage", { name: nurse.firstName })} />}
					onClick={() => track("wage_breakdown_open_nurse_profile", { department_id: departmentId, nurse_id: nurse.id })}
					dataTestId="wage-breakdown-footer-profile-link"
					emphasis="strong"
					backToOrigin
				/>
			)}
			{showRates && <FooterRatesLink isAgency={isAgency} />}
		</Box>
	);
};
